'use client';

import { c as _c } from "react/compiler-runtime";
import { useI18n } from '@/context/I18nProvider';
import { cn } from '@/lib/utils';
import { EmojiPicker as FrimousseEmojiPicker } from 'frimousse';
import { ComponentProps, ReactNode, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
type EmojiPickerProps = {
  children: ReactNode;
  onEmojiSelect: (emoji: string) => void;
  disabled?: boolean;
  side?: ComponentProps<typeof PopoverContent>['side'];
  align?: ComponentProps<typeof PopoverContent>['align'];
  className?: string;
  closeOnSelect?: boolean;
};
const EmojiPicker = t0 => {
  const $ = _c(43);
  const {
    children,
    onEmojiSelect,
    disabled: t1,
    side: t2,
    align: t3,
    className,
    closeOnSelect: t4
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const side = t2 === undefined ? "top" : t2;
  const align = t3 === undefined ? "start" : t3;
  const closeOnSelect = t4 === undefined ? true : t4;
  const {
    t
  } = useI18n();
  const [open, setOpen] = useState(false);
  let t5;
  if ($[0] !== children || $[1] !== disabled) {
    t5 = <PopoverTrigger asChild={true} disabled={disabled}>{children}</PopoverTrigger>;
    $[0] = children;
    $[1] = disabled;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== className) {
    t6 = cn("w-fit p-0 border-none shadow-lg", className);
    $[3] = className;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== closeOnSelect || $[6] !== onEmojiSelect) {
    t7 = emoji => {
      onEmojiSelect(emoji.emoji);
      if (closeOnSelect) {
        setOpen(false);
      }
    };
    $[5] = closeOnSelect;
    $[6] = onEmojiSelect;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t) {
    t8 = t("ui.searchEmoji");
    $[8] = t;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t8) {
    t9 = <FrimousseEmojiPicker.Search className="mx-2 mt-2 flex h-9 items-center gap-1.5 rounded-md border bg-transparent px-2.5 text-sm outline-none placeholder:text-muted-foreground" placeholder={t8} />;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t) {
    t10 = t("ui.loading");
    $[12] = t;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10) {
    t11 = <FrimousseEmojiPicker.Loading className="flex h-full items-center justify-center text-sm text-muted-foreground">{t10}</FrimousseEmojiPicker.Loading>;
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t) {
    t12 = t("ui.noEmojiFound");
    $[16] = t;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t12) {
    t13 = <FrimousseEmojiPicker.Empty className="flex h-full items-center justify-center text-sm text-muted-foreground">{t12}</FrimousseEmojiPicker.Empty>;
    $[18] = t12;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <FrimousseEmojiPicker.List className="select-none" components={{
      CategoryHeader: _temp,
      Emoji: _temp2
    }} />;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t11 || $[22] !== t13) {
    t15 = <FrimousseEmojiPicker.Viewport className="flex-1 overflow-y-auto px-1.5 py-1">{t11}{t13}{t14}</FrimousseEmojiPicker.Viewport>;
    $[21] = t11;
    $[22] = t13;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t) {
    t16 = <FrimousseEmojiPicker.ActiveEmoji>{t17 => {
        const {
          emoji: emoji_1
        } = t17;
        return <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0 flex-1">{emoji_1 ? <><span className="text-lg">{emoji_1.emoji}</span><span className="truncate">{emoji_1.label}</span></> : <span>{t("ui.selectEmoji")}</span>}</div>;
      }}</FrimousseEmojiPicker.ActiveEmoji>;
    $[24] = t;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <FrimousseEmojiPicker.SkinToneSelector className="flex size-7 items-center justify-center rounded text-lg hover:bg-accent transition-colors" />;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== t16) {
    t18 = <div className="flex items-center justify-between border-t px-2 py-2">{t16}{t17}</div>;
    $[27] = t16;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== t15 || $[30] !== t18 || $[31] !== t7 || $[32] !== t9) {
    t19 = <FrimousseEmojiPicker.Root onEmojiSelect={t7} className="isolate flex h-[368px] w-fit flex-col bg-popover text-popover-foreground rounded-lg border">{t9}{t15}{t18}</FrimousseEmojiPicker.Root>;
    $[29] = t15;
    $[30] = t18;
    $[31] = t7;
    $[32] = t9;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== align || $[35] !== side || $[36] !== t19 || $[37] !== t6) {
    t20 = <PopoverContent side={side} align={align} className={t6}>{t19}</PopoverContent>;
    $[34] = align;
    $[35] = side;
    $[36] = t19;
    $[37] = t6;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== open || $[40] !== t20 || $[41] !== t5) {
    t21 = <Popover open={open} onOpenChange={setOpen}>{t5}{t20}</Popover>;
    $[39] = open;
    $[40] = t20;
    $[41] = t5;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  return t21;
};
export { EmojiPicker };
function _temp(t0) {
  const {
    category
  } = t0;
  return <div className="px-1 pb-1.5 pt-3 text-xs font-medium text-muted-foreground">{category.label}</div>;
}
function _temp2(t0) {
  const {
    emoji: emoji_0,
    ...props
  } = t0;
  return <button type="button" className="flex size-8 items-center justify-center rounded text-lg hover:bg-accent transition-colors" {...props}>{emoji_0.emoji}</button>;
}